import { h } from '@omni-torn/ui';
import { server } from '../session';
import { box, withTableCsv } from '../ui';

/**
 * The "Server" box — owner only (owner's ask 2026-11-02: "Do you have any way to see the servers current capacity? Any way
 * to warn me when its running low or needs an upgrade?"). From GET /api/owner/capacity (apps/api/src/capacity.ts): gauges
 * for the disk, memory and CPU, the biggest data files with their growth a day, days until the disk is full, a plain-words
 * verdict and the warnings sent to him (with "Mark as seen" — part of the Owner menu badge). Shown on the Owner tab and on
 * Bot stats.
 */

interface DataFile {
  name: string;
  bytes: number;
  folder: boolean;
  capped?: boolean;
  perDay: number | null;
}
export interface CapacityReport {
  at: number;
  disk: { total: number; used: number; free: number; pct: number };
  memory: { total: number; used: number; pct: number; apiRss: number; botRss: number | null };
  cpu: { load1: number; load5: number; load15: number; cores: number; pct: number };
  uptime: { server: number; api: number };
  files: DataFile[];
  growth: { d7: number | null; d30: number | null };
  daysLeft: number | null;
  pressure: { memory: boolean; cpu: boolean };
  verdict: { level: 'fine' | 'plan' | 'now'; weeks: number | null; text: string; next: string | null; reasons: string[] };
  notices: Array<{ kind: string; level: number; at: number; text: string }>;
  unseen: number;
  history: Array<{ day: string; diskUsed: number; diskTotal: number; pct: number }>;
  daysKept: number;
  thresholds: { disk: number[]; daysLeft: number[]; memoryPct: number; sustainMinutes: number; repeatHours: number };
}

const GB = 1024 ** 3;
const MB = 1024 ** 2;
export function bytes(b: number): string {
  const a = Math.abs(b);
  if (a >= GB) return `${(b / GB).toFixed(a >= 10 * GB ? 0 : 1)} GB`;
  if (a >= MB) return `${(b / MB).toFixed(a >= 10 * MB ? 0 : 1)} MB`;
  return `${Math.round(b / 1024)} KB`;
}
const perDay = (b: number | null) => (b === null ? '—' : `${b >= 0 ? '+' : ''}${bytes(b)} a day`);
function since(s: number): string {
  const d = Math.floor(s / 86_400);
  const hr = Math.floor((s % 86_400) / 3600);
  return d ? `${d} d ${hr} h` : `${hr} h ${Math.floor((s % 3600) / 60)} min`;
}

/** A gauge: label, a bar filled to `pct` (amber from `warn`, red from `bad`), the words under it. */
function gauge(label: string, pct: number, words: string, field: string, warn: number, bad: number): HTMLElement {
  const fill = h('span', { class: `cap-fill${pct >= bad ? ' bad' : pct >= warn ? ' warn' : ''}` });
  fill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  return h(
    'div',
    { class: 'cap-gauge', 'data-gauge': field },
    h('div', { class: 'cap-gauge-head' }, h('strong', {}, label), h('span', { class: 'cap-pct', 'data-field': `${field}-pct` }, `${Math.round(pct)}%`)),
    h('span', { class: 'cap-track', role: 'meter', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(Math.round(pct)), 'aria-label': label }, fill),
    h('div', { class: 'muted small' }, words),
  );
}

// The Owner menu badge adds these to the bot's notices (owner.ts ownerBadge).
let unseenNow = 0;
export const capacityUnseen = () => unseenNow;
export function setCapacityUnseen(n: number): void {
  unseenNow = n;
}

export function serverCapacityBox(onSeen?: () => void): HTMLElement {
  const body = h('div', { 'data-field': 'capacity' }, h('p', { class: 'muted' }, 'Loading…'));
  const draw = (x: CapacityReport) => {
    unseenNow = x.unseen;
    const v = x.verdict;
    const seen = h('button', { type: 'button', class: 'btn-small', 'data-action': 'capacity-seen', hidden: !x.unseen }, 'Mark as seen') as HTMLButtonElement;
    seen.addEventListener('click', () =>
      void server('POST', '/api/owner/capacity/seen').then(() => {
        unseenNow = 0;
        onSeen?.();
        void load(false);
      }),
    );
    const again = h('button', { type: 'button', class: 'btn-small', 'data-action': 'capacity-refresh' }, 'Check now') as HTMLButtonElement;
    again.addEventListener('click', () => void load(true));
    const t = x.thresholds;
    body.replaceChildren(
      h('p', { class: `notice${v.level === 'now' ? ' bad' : v.level === 'fine' ? ' ask' : ''}`, 'data-field': 'verdict', 'data-level': v.level }, h('strong', {}, v.text), v.next ? h('span', {}, ` Next step: ${v.next}.`) : null),
      h(
        'div',
        { class: 'cap-gauges' },
        gauge('Disk', x.disk.pct, `${bytes(x.disk.used)} used · ${bytes(x.disk.free)} free of ${bytes(x.disk.total)}`, 'disk', t.disk[0] ?? 70, t.disk[1] ?? 85),
        gauge('Memory', x.memory.pct, `${bytes(x.memory.used)} of ${bytes(x.memory.total)} · API ${bytes(x.memory.apiRss)}${x.memory.botRss === null ? ' · bot not answering' : ` · bot ${bytes(x.memory.botRss)}`}${x.pressure.memory ? ' · high for 30 min' : ''}`, 'memory', t.memoryPct - 10, t.memoryPct),
        gauge('CPU', x.cpu.pct, `load ${x.cpu.load1} · ${x.cpu.load5} · ${x.cpu.load15} (1 · 5 · 15 min) on ${x.cpu.cores} core${x.cpu.cores === 1 ? '' : 's'}${x.pressure.cpu ? ' · above the cores for 30 min' : ''}`, 'cpu', 70, 100),
      ),
      h(
        'div',
        { class: 'bot-health-grid' },
        ...(
          [
            ['disk growth a day (last 7 days)', perDay(x.growth.d7), 'growth-7'],
            ['disk growth a day (last 30 days)', perDay(x.growth.d30), 'growth-30'],
            ['days until the disk is full', x.daysLeft === null ? (x.daysKept < 2 ? 'after a day of history' : 'not filling') : `about ${Math.round(x.daysLeft)}`, 'days-left'],
            ['server up', since(x.uptime.server), 'uptime'],
            ['API up', since(x.uptime.api), 'api-uptime'],
            ['days of history', String(x.daysKept), 'days-kept'],
          ] as Array<[string, string, string]>
        ).map(([label, value, field]) => h('div', { 'data-stat': field }, h('strong', {}, value), h('span', { class: 'muted small' }, label))),
      ),
      h('h3', {}, 'Biggest data files'),
      x.files.length
        ? withTableCsv(h(
            'div',
            { class: 'table-wrap' },
            h(
              'table',
              { class: 'usage-table', 'data-field': 'cap-files' },
              h('thead', {}, h('tr', {}, h('th', {}, 'File or folder'), h('th', { class: 'num' }, 'Size'), h('th', { class: 'num' }, 'Growth (7 days)'))),
              h('tbody', {}, ...x.files.map((f) => h('tr', { 'data-file': f.name }, h('th', {}, f.folder ? `${f.name}/` : f.name), h('td', { class: 'num' }, `${f.capped ? '≥ ' : ''}${bytes(f.bytes)}`), h('td', { class: 'num' }, perDay(f.perDay))))),
            ),
          ))
        : h('p', { class: 'muted', 'data-field': 'cap-files' }, 'No data files yet.'),
      h('h3', {}, 'Warnings ', x.unseen ? h('span', { class: 'nav-badge' }, String(x.unseen)) : null),
      x.notices.length
        ? h('ul', { class: 'plain-list', 'data-field': 'cap-notices' }, ...x.notices.map((n) => h('li', { 'data-notice': n.kind }, h('span', { class: 'muted small' }, `${new Date(n.at).toLocaleString()} — `), n.text)))
        : h('p', { class: 'muted', 'data-field': 'cap-notices' }, 'None so far.'),
      h('div', { class: 'srv-actions' }, seen, again),
      h(
        'p',
        { class: 'muted small' },
        `Checked every 5 minutes (last ${new Date(x.at).toLocaleTimeString()}). You’re warned on the Owner tab and in your Discord log channel when the disk passes ${t.disk.join(' / ')} % used, when it would be full in under ${t.daysLeft.join(' / ')} days, when memory stays over ${t.memoryPct} % or the load above the cores for ${t.sustainMinutes} minutes — each level once (at most once every ${t.repeatHours} hours per kind). How to upgrade: docs/hosting.md → “Capacity and upgrades”.`,
      ),
    );
  };
  const load = (fresh: boolean) =>
    server<CapacityReport>('GET', `/api/owner/capacity${fresh ? '?fresh=1' : ''}`)
      .then(draw)
      .catch((err: unknown) => body.replaceChildren(h('p', { class: 'notice bad' }, (err as Error).message)));
  void load(false);
  return box('Server', [h('p', { class: 'muted' }, 'The server’s room to grow: disk, memory and CPU, what takes the space, and when to upgrade. Numbers only.'), body], { id: 'server-capacity' });
}
